import Logo from "Pages/LoginPage/Logo";
import React, { useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import { useSelector } from "react-redux";
import { getMainTabIconComponent } from "../SVGS/MainTabSidebarIcons";
import ActivityIcon from "../assets/SidebarIcons/ActivityIcon";
import BarChartIcon from "../assets/SidebarIcons/BarChartIcon";
import CalendarIcon from "../assets/SidebarIcons/CalendarIcon";
import CheckSquareIcon from "../assets/SidebarIcons/CheckSquareIcon";

import ClockIcon from "../assets/SidebarIcons/ClockIcon";
import CreditCardIcon from "../assets/SidebarIcons/CreditCardIcon";
import EobParserIcon from "../assets/SidebarIcons/EobParserIcon";
import FileIcon from "../assets/SidebarIcons/FileIcon";
import GridIcon from "../assets/SidebarIcons/GridIcon";
import ListIcon from "../assets/SidebarIcons/ListIcon";
import MailIcon from "../assets/SidebarIcons/MailIcon";
import AssetSettingsIcon from "../assets/SidebarIcons/SettingsIcon";
import UploadIcon from "../assets/SidebarIcons/UploadIcon";
import UsersIcon from "../assets/SidebarIcons/UsersIcon";
import WalletIcon from "../assets/SidebarIcons/WalletIcon";
import Practices from "./Header/Practices";
import SidebarCollapseButton from "./SidebarCollapseButton";
import ShortcutManagerPopup from "./ShortcutManagerPopup";
import HoverRevealScrollbarWrapper from "Components/HoverRevealScrollbarWrapper/HoverRevealScrollbarWrapper";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { LockIcon, LogOutIcon, Menu, Pin, Users } from "lucide-react";
import ResetPasswordPopup from "Popups/ResetPasswordPopup/ResetPasswordPopup";
import UserProfilePopup from "Popups/UserProfilePopup/UserProfilePopup";
import {
  SETTINGS_SHORTCUT_ITEMS,
  SETTINGS_SHORTCUTS_BY_KEY,
  readPinnedSettingsShortcuts,
  readSettingsShortcutOrder,
  writePinnedSettingsShortcuts,
  writeSettingsShortcutOrder,
} from "MainTabs/Settings2/settingsShortcutsConfig";

const sidebarIconMap = {
  grid: GridIcon,
  users: UsersIcon,
  file: FileIcon,
  activity: ActivityIcon,
  list: ListIcon,
  calendar: CalendarIcon,
  "user-plus": null,
  message: null,
  "check-square": CheckSquareIcon,
  upload: UploadIcon,
  clock: ClockIcon,
  "credit-card": CreditCardIcon,
  "eob-parser": EobParserIcon,
  wallet: WalletIcon,
  mail: MailIcon,
  download: null,
  layers: null,
  shuffle: null,
  "bar-chart": BarChartIcon,
  settings: AssetSettingsIcon,
};

const restrictedAccessEmails = [
  "cneal@southlandneuromuscular.com",
  "citlalytgarcia86@gmail.com",
];

const getSidebarGroup = (title) => {
  const payments = [
    "Insurance Payments",
    "EOB Parser",
    "Patient Payments",
    "Patient Statements",
  ];
  const operations = [
    "Data Import",
    "Tasks",
    "Eligibility",
    "Onboarding",
    "Scheduling",
  ];
  const tools = ["Reports", "New Reports"];
  if (payments.includes(title)) return "Payments";
  if (operations.includes(title)) return "Operations";
  if (tools.includes(title)) return "Tools";
  return null;
};

const buildSections = (tabs) => {
  if (!tabs.length) return [];
  const sections = [];
  let currentGroup = getSidebarGroup(tabs[0].title);
  let currentItems = [tabs[0]];
  for (let i = 1; i < tabs.length; i += 1) {
    const tab = tabs[i];
    const g = getSidebarGroup(tab.title);
    if (g !== currentGroup) {
      sections.push({ group: currentGroup, items: currentItems });
      currentGroup = g;
      currentItems = [tab];
    } else {
      currentItems.push(tab);
    }
  }
  sections.push({ group: currentGroup, items: currentItems });
  return sections;
};

const s = {
  sidebar: (collapsed) => ({
    width: collapsed ? 52 : 220,
    background: "#fff",
    borderRight: "1px solid #EBEBEF",
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    overflowY: "hidden",
    overflowX: "hidden",
    transition: "width .25s cubic-bezier(.4,0,.2,1)",
    flexShrink: 0,
    position: "relative",
    userSelect: "none",
  }),
  logo: (collapsed) => ({
    padding: collapsed ? "6px 0" : "10px 10px 2px",
    borderBottom: "1px solid #EBEBEF",
    display: "flex",
    justifyContent: collapsed ? "center" : "flex-start",
    flexShrink: 0,
  }),
  group: { padding: "10px 10px 2px" },
  groupLabel: {
    fontSize: 9.5,
    fontWeight: 600,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "#889AB3",
    padding: "2px 10px 5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  navItem: (active, collapsed) => ({
    display: "flex",
    alignItems: "center",
    gap: collapsed ? 0 : 8,
    padding: collapsed ? "8px" : "6px 10px",
    borderRadius: 6,
    color: active ? "#604FF8" : "#404D60",
    background: active ? "rgba(96,79,248,.06)" : "transparent",
    fontWeight: active ? 500 : 450,
    fontSize: 12,
    cursor: "pointer",
    textDecoration: "none",
    transition: "all .12s ease",
    justifyContent: collapsed ? "center" : "flex-start",
    position: "relative",
    whiteSpace: "nowrap",
    overflow: "hidden",
  }),
  iconWrap: { width: 15, height: 15, flexShrink: 0, display: "inline-flex" },
  footer: {
    marginTop: "auto",
    padding: "0px 10px",
    borderTop: "1px solid #EBEBEF",
  },
  user: {
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "7px 10px",
    borderRadius: 6,
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "#F6F5FF",//avatar
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    fontWeight: 500,
    color: "#000",
    flexShrink: 0,
  },
  collapseBtn: {
    position: "absolute",
    right: -12,
    top: "50%",
    transform: "translateY(-50%)",
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "#fff",
    border: "1px solid #EBEBEF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 10,
    boxShadow: "0 1px 3px rgba(21,26,32,.08)",
  },
};

const renderTabIcon = (tab) => {
  const AssetIcon = typeof tab.icon === "string" ? sidebarIconMap[tab.icon] : null;
  if (AssetIcon) {
    return (
      <span style={s.iconWrap}>
        <span
          style={{
            width: "100%",
            height: "100%",
            display: "inline-flex",
            color: "currentColor",
          }}
        >
          <AssetIcon />
        </span>
      </span>
    );
  }
  const LegacyIcon = getMainTabIconComponent(tab.title);
  if (LegacyIcon) {
    return (
      <span style={s.iconWrap}>
        <LegacyIcon className="w-[15px] h-[15px]" />
      </span>
    );
  }
  if (tab.icon && typeof tab.icon !== "string") {
    const Icon = tab.icon;
    return (
      <span style={s.iconWrap}>
        <Icon className="w-[15px] h-[15px]" />
      </span>
    );
  }
  return (
    <img
      className="object-contain"
      style={{ width: 15, height: 15 }}
      src={tab.icon}
      alt=""
    />
  );
};

const SidebarNavItem = ({
  tab,
  collapsed,
  activeTab,
  onItemClick,
  badgeCount = 0,
}) => {
  const [hovered, setHovered] = useState(false);
  const isActive = activeTab === tab.key;
  const base = s.navItem(isActive, collapsed);
  const style = {
    ...base,
    background:
      hovered && !isActive
        ? "#F3F4F7"
        : isActive
          ? "rgba(96,79,248,.06)"
          : "transparent",
    color: isActive ? "#604FF8" : hovered ? "#151A20" : "#404D60",
  };

  return (
    <div
      role="button"
      tabIndex={0}
      data-tooltip-id={collapsed ? "sidebar-tab-tooltip" : undefined}
      data-tooltip-content={
        collapsed ? tab.title === "Charges" ? "Claims" : tab.title : undefined
      }
      style={style}
      onClick={() =>
        onItemClick(tab.key, tab.name, tab.right, tab.title, tab.data)
      }
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onItemClick(tab.key, tab.name, tab.right, tab.title, tab.data);
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {renderTabIcon(tab)}
      {!collapsed && <span style={{ flex: 1 }}>{tab.title === "Charges" ? "Claims" : tab.title}</span>}
      {!collapsed && badgeCount > 0 && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 18,
            height: 18,
            padding: "0 5px",
            borderRadius: 9,
            background: "#EF4444",
            color: "#fff",
            fontSize: 10,
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {badgeCount}
        </span>
      )}
      {collapsed && badgeCount > 0 && (
        <span
          style={{
            position: "absolute",
            top: 2,
            right: 2,
            minWidth: 14,
            height: 14,
            padding: "0 3px",
            borderRadius: 7,
            background: "#EF4444",
            color: "#fff",
            fontSize: 9,
            fontWeight: 600,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            lineHeight: 1,
          }}
        >
          {badgeCount}
        </span>
      )}
    </div>
  );
};

const ShortcutNavItem = ({
  tab,
  collapsed,
  active,
  onNavigate,
  onUnpin,
}) => {
  const [hovered, setHovered] = useState(false);
  const isActive = active;
  const base = s.navItem(isActive, collapsed);
  const style = {
    ...base,
    paddingRight: !collapsed ? 34 : base.padding,
    background:
      hovered && !isActive
        ? "#F3F4F7"
        : isActive
          ? "rgba(96,79,248,.06)"
          : "transparent",
    color: isActive ? "#604FF8" : hovered ? "#151A20" : "#404D60",
  };

  return (
    <div
      role="button"
      tabIndex={0}
      data-tooltip-id={collapsed ? "sidebar-tab-tooltip" : undefined}
      data-tooltip-content={collapsed ? tab.title : undefined}
      style={style}
      onClick={onNavigate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onNavigate();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {renderTabIcon(tab)}
      {!collapsed && <span style={{ flex: 1 }}>{tab.title}</span>}
      {!collapsed && hovered && (
        <span
          style={{
            position: "absolute",
            right: 8,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <button
            type="button"
            style={{
              border: "none",
              background: "transparent",
              color: "#604FF8",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: 2,
            }}
            onClick={(event) => {
              event.stopPropagation();
              onUnpin(tab.shortcutKey);
            }}
          >
            <Pin size={13} fill="currentColor" />
          </button>
        </span>
      )}
    </div>
  );
};

const Sidebar = ({ mainTabs, activeTab, onItemClick }) => {
  const [resetPopup, setResetPopup] = useState(false);
  const [profilePopup, setProfilePopup] = useState(false);
  const { userinfo } = useSelector((state) => state.userAuth);
  const [collapsed, setCollapsed] = useState(false);
  const [pinnedShortcutKeys, setPinnedShortcutKeys] = useState([]);
  const [shortcutOrderKeys, setShortcutOrderKeys] = useState([]);
  const [isManagePopupOpen, setIsManagePopupOpen] = useState(false);
  const [isShortcutsHeaderHovered, setIsShortcutsHeaderHovered] = useState(false);

  const userModuleRights =
    useSelector((state) => state.userAuth.userinfo.userModuleRights) ?? {};
  const requestedSettingsShortcutKey = useSelector(
    (state) => state.tabs?.mainTab?.data?.settingsShortcutTab,
  );

  const intakeActionRequiredCount = useSelector(
    (state) => state.intakeWorklist?.actionRequiredCount ?? 0,
  );

  const getBadgeCount = (tab) =>
    tab.title === "Onboarding" ? intakeActionRequiredCount : 0;

  const allowedRights = (tab) =>
    userModuleRights[tab.navigationName] ?? false;

  const filteredTabs = mainTabs
    .filter(allowedRights)
    .filter(
      (tab) =>
        !["Settings", process.env.REACT_APP_DEVELOPMENT ? "" : "Denial"].includes(
          tab.title,
        ),
    )
    .filter((tab) => {
      if (restrictedAccessEmails.includes(userinfo.userEmail.toLowerCase())) {
        return tab.title !== "Dashboard";
      }
      return true;
    });

  useEffect(() => {
    const syncPinnedShortcuts = () => {
      setPinnedShortcutKeys(readPinnedSettingsShortcuts(userinfo));
      setShortcutOrderKeys(readSettingsShortcutOrder(userinfo));
    };

    syncPinnedShortcuts();
    window.addEventListener("settings-shortcuts-updated", syncPinnedShortcuts);
    window.addEventListener("storage", syncPinnedShortcuts);

    return () => {
      window.removeEventListener("settings-shortcuts-updated", syncPinnedShortcuts);
      window.removeEventListener("storage", syncPinnedShortcuts);
    };
  }, [userinfo]);

  const settingsTab = mainTabs.find((tab) => tab.title === "Settings");
  const hasSettingsAccess = settingsTab
    ? userModuleRights[settingsTab.navigationName] ?? false
    : false;
  const activeSettingsShortcutKey =
    activeTab === settingsTab?.key ? requestedSettingsShortcutKey : null;

  const orderedShortcutItems = useMemo(
    () =>
      shortcutOrderKeys
        .map((key) => SETTINGS_SHORTCUTS_BY_KEY[key])
        .filter(Boolean)
        .map((item) => ({
          ...item,
          key: settingsTab?.key,
          shortcutKey: item.key,
          name: settingsTab?.name ?? "settings2",
          right: true,
          data: { settingsShortcutTab: item.key },
        })),
    [shortcutOrderKeys, settingsTab],
  );

  const allShortcutTabs = useMemo(() => {
    if (orderedShortcutItems.length) return orderedShortcutItems;
    return SETTINGS_SHORTCUT_ITEMS.map((item) => ({
      ...item,
      key: settingsTab?.key,
      shortcutKey: item.key,
      name: settingsTab?.name ?? "settings2",
      right: true,
      data: { settingsShortcutTab: item.key },
    }));
  }, [orderedShortcutItems, settingsTab]);

  const shortcutTabs = useMemo(
    () =>
      orderedShortcutItems.filter((item) =>
        pinnedShortcutKeys.includes(item.shortcutKey),
      ),
    [orderedShortcutItems, pinnedShortcutKeys],
  );

  const persistShortcuts = (nextKeys) => {
    const persisted = writePinnedSettingsShortcuts(userinfo, nextKeys);
    setPinnedShortcutKeys(persisted);
    window.dispatchEvent(new Event("settings-shortcuts-updated"));
  };

  const persistShortcutOrder = (nextOrderKeys) => {
    const persisted = writeSettingsShortcutOrder(userinfo, nextOrderKeys);
    setShortcutOrderKeys(persisted);
    window.dispatchEvent(new Event("settings-shortcuts-updated"));
  };

  const unpinShortcut = (shortcutKey) => {
    if (!pinnedShortcutKeys.includes(shortcutKey)) return;
    persistShortcuts(pinnedShortcutKeys.filter((key) => key !== shortcutKey));
  };

  const reorderShortcuts = (nextKeys) => {
    persistShortcutOrder(nextKeys);
  };

  const toggleShortcutPin = (shortcutKey) => {
    if (!SETTINGS_SHORTCUTS_BY_KEY[shortcutKey]) return;
    if (pinnedShortcutKeys.includes(shortcutKey)) {
      persistShortcuts(pinnedShortcutKeys.filter((key) => key !== shortcutKey));
      return;
    }
    persistShortcuts([...pinnedShortcutKeys, shortcutKey]);
  };

  const selectAllShortcuts = () => {
    persistShortcuts(allShortcutTabs.map((tab) => tab.shortcutKey));
  };

  const deselectAllShortcuts = () => {
    persistShortcuts([]);
  };

  const sections = buildSections(filteredTabs);

  const initials = useMemo(() => {
    const name = userinfo?.userName || "";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase() || "—";
  }, [userinfo?.userName]);

  const onHandleLogout = () => {
    window.location.reload();
  };

  const openResetPopup = () => {
    setResetPopup(true);
  };

  const closeResetPopup = () => {
    setResetPopup(false);
  };

  const onOpenProfilePopup = () => {
    setProfilePopup(true);
  };

  const onCloseProfilePopup = () => {
    setProfilePopup(false);
  };

  if (Object.keys(userModuleRights).length === 0) return <></>;

  return (
    <div style={{ position: "relative" }}>
      {resetPopup && <ResetPasswordPopup onClose={closeResetPopup} />}
      {profilePopup && <UserProfilePopup onClose={onCloseProfilePopup} />}
      <Tooltip id="sidebar-tab-tooltip" style={{ zIndex: 10 }} />
      <div
        id="sidebar"
        style={s.sidebar(collapsed)}
        className="font-sans text-[13px] antialiased]"
      >
        <div style={s.logo(collapsed)}>
          <div style={{ display: collapsed ? "block" : "none" }}>
            <Logo
              src="/trucare-favicon.png"
              className="object-cover object-left size-12"
            />
          </div>
          <div
            className="w-full"
            style={{ display: collapsed ? "none" : "block" }}
          >
            <Practices />
          </div>
        </div>

        <HoverRevealScrollbarWrapper className="flex-1 min-h-0 overflow-hidden">
          <div
            data-hover-scrollable="true"
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {sections.map((section, si) => (
              <div key={si} style={s.group}>
                {section.group != null && !collapsed && (
                  <div style={s.groupLabel}>{section.group}</div>
                )}
                {section.items.map((tab) => (
                  <SidebarNavItem
                    key={tab.key}
                    tab={tab}
                    collapsed={collapsed}
                    activeTab={activeTab}
                    onItemClick={onItemClick}
                  />
                ))}
              </div>
            ))}

            {hasSettingsAccess && (
              <div style={s.group}>
                {!collapsed && (
                  <div
                    style={s.groupLabel}
                    onMouseEnter={() => setIsShortcutsHeaderHovered(true)}
                    onMouseLeave={() => setIsShortcutsHeaderHovered(false)}
                  >
                    <span>Shortcuts</span>
                    <button
                      type="button"
                      data-tooltip-id="shortcuts-manage-trigger"
                      data-tooltip-content="Manage shortcuts"
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#6B7280",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: isShortcutsHeaderHovered ? "pointer" : "default",
                        padding: 2,
                        opacity: isShortcutsHeaderHovered ? 1 : 0,
                        pointerEvents: isShortcutsHeaderHovered ? "auto" : "none",
                        transition: "opacity .12s ease",
                      }}
                      onClick={() => setIsManagePopupOpen(true)}
                    >
                      <Menu size={14} />
                    </button>
                    <Tooltip
                      id="shortcuts-manage-trigger"
                      place="right"
                      style={{ zIndex: 20, textTransform: "none" }}
                    />
                  </div>
                )}
                {shortcutTabs.map((tab) => (
                  <ShortcutNavItem
                    key={tab.shortcutKey}
                    tab={tab}
                    collapsed={collapsed}
                    active={activeSettingsShortcutKey === tab.shortcutKey}
                    onNavigate={() => {
                      if (!settingsTab) return;
                      onItemClick(
                        settingsTab.key,
                        settingsTab.name,
                        settingsTab.right,
                        settingsTab.title,
                        {
                          ...(settingsTab.data ?? {}),
                          settingsShortcutTab: tab.data.settingsShortcutTab,
                          settingsShortcutNavId: Date.now(),
                        },
                      );
                    }}
                    onUnpin={unpinShortcut}
                  />
                ))}
              </div>
            )}
          </div>
        </HoverRevealScrollbarWrapper>

        <div style={{ ...s.footer, flexShrink: 0 }}>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                style={{
                  ...s.user,
                  justifyContent: collapsed ? "center" : "flex-start",
                  width: "100%",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                }}
              >
                <div style={s.avatar}>{initials}</div>
                {!collapsed && (
                  <div style={{ textAlign: "left" }}>
                    <div
                      style={{
                        fontSize: 12.5,
                        fontWeight: 500,
                        color: "#151A20",
                      }}
                    >
                      {userinfo?.userName || "User"}
                    </div>
                    <div style={{ fontSize: 11, color: "#889AB3" }}>
                      {userinfo?.roleName || ""}
                    </div>
                  </div>
                )}
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                align={collapsed ? "center" : "end"}
                side="top"
                sideOffset={8}
                className="z-50 min-w-[200px] rounded-xl border border-gray-100 bg-white p-1.5 shadow-lg"
              >
                <DropdownMenu.Item
                  onClick={onOpenProfilePopup}
                  className="flex cursor-pointer select-none items-center justify-between rounded-md px-3 py-2 text-sm text-[#3f4754] outline-none transition hover:bg-gray-50 focus:bg-gray-50"
                >
                  <span className="flex items-center gap-2.5">
                    <Users size={14} className="text-gray-500 " />
                    Profile
                  </span>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onClick={openResetPopup}
                  className="flex cursor-pointer select-none items-center justify-between rounded-md px-3 py-2 text-sm text-[#3f4754] outline-none transition hover:bg-gray-50 focus:bg-gray-50"
                >
                  <span className="flex items-center gap-2.5">
                    <LockIcon size={14} className="text-gray-500 " />
                    Reset Password
                  </span>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  onClick={onHandleLogout}
                  className="flex cursor-pointer select-none items-center justify-between rounded-md px-3 py-2 text-sm text-[#3f4754] outline-none transition hover:bg-gray-50 focus:bg-gray-50"
                >
                  <span className="flex items-center gap-2.5">
                    <LogOutIcon size={14} className="text-gray-500 " />
                    Sign out
                  </span>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>
      </div>
      <SidebarCollapseButton s={s} collapsed={collapsed} setCollapsed={setCollapsed} />
      <ShortcutManagerPopup
        isOpen={isManagePopupOpen}
        shortcuts={allShortcutTabs}
        pinnedShortcutKeys={pinnedShortcutKeys}
        onClose={() => setIsManagePopupOpen(false)}
        onTogglePin={toggleShortcutPin}
        onSelectAll={selectAllShortcuts}
        onDeselectAll={deselectAllShortcuts}
        onReorder={reorderShortcuts}
        renderIcon={renderTabIcon}
      />

    </div>


  );
};

export default Sidebar;
